import { Button } from "@tennr/frontend/components/Button/Button";
import { motion, MotionProps } from "framer-motion";
import { Link } from "@tanstack/react-router";

import "./HeroCopy.css";
import { InView } from "@tennr/frontend/components/InView";
import { FC, useState, useEffect, ReactNode, Children } from "react";
import { Fragment } from "react/jsx-runtime";
import { useMobile } from "@tennr/frontend/hooks/useMobile";

interface HeroCopyProps {
  logoMotion?: MotionProps;
  headingMotion?: MotionProps;
  bodyMotion?: MotionProps;
  title?: string;
  heading?: string;
  bodyCopy?: string;
  ctaText?: string;
  ctaLink?: string;
  headingSize?: string;
  ctaWidth?: number | string;
  disableMotion?: boolean;
  children?: ReactNode;
}
function ConditionalLink({ to, children }: { to?: string; children: React.ReactNode }) {
  return to ? <Link to={to}>{children}</Link> : children;
}

export const HeroCopy: FC<HeroCopyProps> = ({
  bodyMotion,
  headingMotion,
  title = "BETTER INTAKE STARTS NOW",
  heading = "Convert every\npatient, every time",
  bodyCopy = "Ensure payor-ready documentation, boost conversions, and cut\nfirst\u2011pass denials. Tennr takes you from fax to first visit, faster.",
  ctaText,
  ctaWidth = 254,
  ctaLink,
  disableMotion,
  children,
}) => {
  const [wrapBodyText, setWrapBodyText] = useState(window.innerWidth < 412);
  const isMobile = useMobile();
  useEffect(() => {
    const handleResize = () => {
      setWrapBodyText(window.innerWidth < 412);
    };
    window.addEventListener("resize", handleResize);
  }, []);
  return (
    <div className="hero-copy">
      <div className="text-container">
        {/* Title commented out */}
        <InView
          animation={disableMotion ? "none" : "fadeUp"}
          once={true}
          amount={0.2}
          transitionProperties={
            disableMotion
              ? {}
              : {
                  duration: 1.8,
                  ease: "easeInOut",
                  delay: 1,
                  type: "spring",
                  bounce: 0.4,
                }
          }
        >
          <h4 className="hero-heading-title heading-title">{title}</h4>

          <motion.h1 className={`hero-heading heading-text heading-text--hero`} {...headingMotion}>
            {heading.split("\n").map((line, i) => (
              <Fragment key={i}>
                {line}
                {i < heading.split("\n").length - 1 && <br />}
              </Fragment>
            ))}
          </motion.h1>
        </InView>
        {/* <motion.p className="hero-body" {...bodyMotion}> */}
        <InView
          animation={disableMotion ? "none" : "fadeUp"}
          once={true}
          amount={0.2}
          transitionProperties={
            disableMotion
              ? {}
              : {
                  duration: 1.4,
                  ease: "easeInOut",
                  delay: 1.2,
                  type: "spring",
                  bounce: 0.4,
                }
          }
        >
          <motion.div
            className="heading-body hero-body"
            {...bodyMotion}
            style={{ textWrap: isMobile ? (wrapBodyText ? "balance" : "pretty") : "wrap" }}
          >
            {children ? children : bodyCopy}
          </motion.div>
        </InView>
      </div>
      <InView
        animation={disableMotion ? "none" : "fadeUp"}
        once={true}
        amount={0.2}
        transitionProperties={
          disableMotion
            ? {}
            : {
                duration: 1.7,
                ease: "easeInOut",
                delay: 1.2,
                type: "spring",
                bounce: 0.4,
              }
        }
      >
        <ConditionalLink to={ctaLink || "/book"}>
          {ctaText && (
            <Button text={ctaText} minWidth={ctaWidth} className="hero-cta-button" dontAnimateIntro={disableMotion} />
          )}
        </ConditionalLink>
      </InView>
    </div>
  );
};
